import { haptics } from './haptics';
import type { PrefsStore } from '@/stores/PrefsStore';

// AudioContext is lazy — created on first user gesture
let ctx: AudioContext | null = null;
let unlocked = false;

function getCtx(): AudioContext | null {
  if (!unlocked) return null;
  if (!ctx) {
    try { ctx = new AudioContext(); } catch { return null; }
  }
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

// Unlock AudioContext on first user gesture (browsers require it)
function unlockOnGesture() {
  if (unlocked) return;
  const unlock = () => {
    unlocked = true;
    getCtx();
    document.removeEventListener('click', unlock, true);
    document.removeEventListener('pointerdown', unlock, true);
  };
  document.addEventListener('click', unlock, true);
  document.addEventListener('pointerdown', unlock, true);
}

interface ToneOpts {
  freq: number;
  freq2?: number;      // sweep end frequency
  dur: number;         // seconds
  type?: OscillatorType;
  gain?: number;       // 0..1, default 0.18
  delayMs?: number;    // schedule delay in ms
}

function tone(opts: ToneOpts): void {
  const ac = getCtx();
  if (!ac) return;
  const { freq, freq2, dur, type = 'sine', gain = 0.18, delayMs = 0 } = opts;
  const start = ac.currentTime + delayMs / 1000;
  const osc = ac.createOscillator();
  const env = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (freq2 !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(freq2, 1), start + dur * 0.9);
  }
  env.gain.setValueAtTime(0.001, start);
  env.gain.exponentialRampToValueAtTime(gain, start + 0.012);
  env.gain.exponentialRampToValueAtTime(0.001, start + dur);
  osc.connect(env);
  env.connect(ac.destination);
  osc.start(start);
  osc.stop(start + dur + 0.01);
}

// ── Haptics mapping (Telegram-typed) ─────────────────────────────────────────

type HapticFn = () => void;

const vibTap: HapticFn = () => haptics.selection();
const vibAccept: HapticFn = () => haptics.success();
const vibReject: HapticFn = () => {
  haptics.impactMedium();
  setTimeout(() => haptics.impactLight(), 34);
  setTimeout(() => haptics.impactMedium(), 68);
};
const vibCount: HapticFn = () => haptics.impactMedium();
const vibGo: HapticFn = () => {
  haptics.impactLight();
  setTimeout(() => haptics.impactMedium(), 30);
  setTimeout(() => haptics.impactMedium(), 64);
};
const vibWin: HapticFn = () => {
  haptics.success();
  setTimeout(() => haptics.impactLight(), 40);
  setTimeout(() => haptics.impactLight(), 80);
  setTimeout(() => haptics.success(), 120);
};
const vibNotify: HapticFn = () => {
  haptics.impactLight();
  setTimeout(() => haptics.impactLight(), 20);
  setTimeout(() => haptics.impactLight(), 30);
};
const vibBig: HapticFn = () => {
  haptics.impactMedium();
  setTimeout(() => haptics.impactMedium(), 38);
  setTimeout(() => haptics.impactMedium(), 60);
};

// ── Feedback engine ───────────────────────────────────────────────────────────

let prefs: PrefsStore | null = null;

function sound(fn: () => void) {
  if (prefs?.sound !== false) fn();
}
function vib(fn: HapticFn) {
  if (prefs?.haptics !== false) fn();
}

export const fx = {
  /** Call once at app init (after store is created) */
  init(store: PrefsStore) {
    prefs = store;
    unlockOnGesture();
  },

  tap() {
    sound(() => tone({ freq: 210, freq2: 150, dur: 0.05, gain: 0.10 }));
    vib(vibTap);
  },

  accept(big = false) {
    if (big) {
      sound(() => {
        tone({ freq: 660, dur: 0.12 });
        tone({ freq: 990, dur: 0.12, delayMs: 70 });
        tone({ freq: 1320, dur: 0.12, delayMs: 140 });
      });
      vib(vibBig);
    } else {
      sound(() => {
        tone({ freq: 660, dur: 0.10 });
        tone({ freq: 880, dur: 0.10, delayMs: 70 });
      });
      vib(vibAccept);
    }
  },

  reject() {
    sound(() => tone({ freq: 175, freq2: 120, dur: 0.18, type: 'sawtooth', gain: 0.14 }));
    vib(vibReject);
  },

  tick() {
    sound(() => tone({ freq: 1300, dur: 0.03, type: 'square', gain: 0.08 }));
    // no haptic for tick — too noisy
  },

  count() {
    sound(() => tone({ freq: 520, dur: 0.13 }));
    vib(vibCount);
  },

  go() {
    sound(() => {
      tone({ freq: 523, dur: 0.14 });
      tone({ freq: 659, dur: 0.14, delayMs: 0 });
      tone({ freq: 784, dur: 0.18, delayMs: 0 });
    });
    vib(vibGo);
  },

  win() {
    sound(() => {
      tone({ freq: 523,  dur: 0.14 });
      tone({ freq: 659,  dur: 0.14, delayMs: 80 });
      tone({ freq: 784,  dur: 0.14, delayMs: 160 });
      tone({ freq: 1046, dur: 0.22, delayMs: 240 });
    });
    vib(vibWin);
  },

  notify() {
    sound(() => {
      tone({ freq: 740, dur: 0.10 });
      tone({ freq: 980, dur: 0.12, delayMs: 80 });
    });
    vib(vibNotify);
  },
};
